import { Component, OnInit, Output, EventEmitter, AfterViewInit, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PurchaseOrderService } from 'src/app/services/purchase-order/purchase-order.service';
import { SupplierService } from 'src/app/services/supplier/supplier.service';
import { FlashMessageService } from 'src/app/shared/services/flash-message.service';
import { AddressService } from 'src/app/services/address/address.service';
import { Observable } from 'rxjs';
import { map, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ModalService } from 'src/app/shared/services/modal.service';
import { MassSuppliersService } from 'src/app/services/mass-suppliers/mass-suppliers.service';
import { RegexEnum } from 'src/app/core/enum/regex.enum';

@Component({
  selector: 'add-supplier',
  templateUrl: './add-supplier.component.html',
  styleUrls: ['./add-supplier.component.scss']
})

export class AddSupplierComponent implements OnInit, AfterViewInit {
  @Output() close = new EventEmitter();
  public newSupplierForm: FormGroup;
  public readOnly: boolean = true;
  public supplierReadOnly: boolean = false;
  public phoneMask = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
  public newLoc: boolean = false;
  public hasAutocompleteResults: boolean = false;
  public hasSupplierAutocompleteResults: boolean = false;
  public supplierResults: any = [];
  public results = [];
  public loading: boolean = false;
  public isEmailUnique: boolean = true;
  public regexEnum = RegexEnum;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private purchaseOrderService: PurchaseOrderService,
    private supplierService: SupplierService,
    private flashMessageService: FlashMessageService,
    private addressService: AddressService,
    private modal: ModalService,
    private _massSuppliersSvc: MassSuppliersService) { }

  ngOnInit() {
    this.addForm();
  }

  ngAfterViewInit() {
    this._setupLocationSearch();
    this._initializecheckExistingEmail();
    this._initializeMatchingSuppliers();
  }

  closeModal() {
    this.modal.close();
  }

  private addForm() {
    this.newSupplierForm = this.fb.group({
      "name": ['', [Validators.required]],
      "status": ["active",[Validators.required]],
      "contactName": ['',[Validators.required]],
      "corporate_email": ['', [Validators.required]],
      "corporate_phone": ['', [Validators.required]],
      "DomainName": "CORP",
      "LocationType": ['CUSTOMER', [Validators.required]],
      "LocationName": ['', [Validators.required]],
      "Address": '',
      "Address1": ['', [Validators.required]],
      "Override": false,
      "City": ['', [Validators.required]],
      "State": ['', [Validators.required]],
      "PostalCode": ['', [Validators.required]],
      "CountryCode": ['USA', [Validators.required]]
    });
  };

  isFieldValid(field: string) {
    return !this.newSupplierForm.get(field).valid && (this.newSupplierForm.get(field).touched || this.newSupplierForm.get(field).dirty);
  }

  overrideLocation() {
    this.readOnly = false;
  }

  fill(location) {
    this.loading = true;
    this.addressService.format(location, this.newSupplierForm.value.CountryCode).then(formattedLoc => {
      let parsedLoc = this.addressService.parseAddress(formattedLoc);
      this.newSupplierForm.patchValue({
        "Address1": parsedLoc.Address1,
        "City": parsedLoc.City,
        "State": parsedLoc.State,
        "PostalCode": parsedLoc.PostalCode
      });
      this.hasAutocompleteResults = false;
      this.loading = false;
    })
  }

  private _setupLocationSearch() {
    Observable.fromEvent(document.getElementById('Address'), 'input').pipe(
      map((e: KeyboardEvent) => {
        let element = e.target as HTMLInputElement;
        let value = element.value;
        return value;
      }),
      debounceTime(750),
      distinctUntilChanged(),
      switchMap(() => {
        let address = this.newSupplierForm.value.Address,
        country = this.newSupplierForm.value.CountryCode;
        if (address.length > 3) {
          return this.addressService.search(address, country)
        } else {
          return new Observable(sub => {
            sub.next([]);
            sub.complete();
          })
        }
      })
    ).subscribe((data: any) => {
      if (!data) {
        this.results = [];
      } else {
        this.hasAutocompleteResults = true;
        this.results = data;
      }
    });
  };

  /**
   * Checks if the user entered Email already exists or not
   */
  private _initializecheckExistingEmail() {
    Observable.fromEvent(document.getElementById('corporate_email'), 'input').pipe(
      map((e: KeyboardEvent) => {
        let element = e.target as HTMLInputElement;
        let value = element.value;
        return value;
      }),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(() => {
        if (!this.newSupplierForm.value.corporate_email) {
          this.isEmailUnique = true
          return null;
        }
        this.loading = true;
        return this._massSuppliersSvc.checkExistingUser(this.newSupplierForm.value.corporate_email)
      })
    ).subscribe((emailValidation: any) => {
      this.isEmailUnique = emailValidation['unique_email'];
    });
  };

  private _initializeMatchingSuppliers() {
    Observable.fromEvent(document.getElementById('name'), 'input').pipe(
      map((e: KeyboardEvent) => {
        let element = e.target as HTMLInputElement;
        let value = element.value;
        return value;
      }),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(() => {
        let name = this.newSupplierForm.value.name;
        if (name != "") {
          return this.supplierService.getMatchingSuppliers(name);
        }
        else {
          this.newSupplierForm.patchValue({
            "name": "",
            "status": "",
            "contactName": "",
            "corporate_email": "",
            "corporate_phone": "",
            "DomainName": "CORP",
            "LocationType": "",
            "LocationName": "",
            "Address": '',
            "Address1": "",
            "Override": false,
            "City": "",
            "State": "",
            "PostalCode": "",
            "CountryCode": ""
          });
          this.supplierReadOnly = false;
          this.hasAutocompleteResults = false;
          this.supplierResults = [];
        }
      })
    ).subscribe((results: any) => {
      this.hasSupplierAutocompleteResults = true;
      this.supplierResults = results.map(function(supplier) {
        return supplier['name'];
      }).join(', ');
    });
  };

  addSupplier() {
    this.loading = true;
    this.supplierService.createSupplier(this.newSupplierForm.value).then(resp => {
      this.closeModal();
      this.loading = false;
      this.flashMessageService.updateMessage("Successfully created supplier", 4000, 'success');
    }).catch(e => {
      this.loading = false;
      this.flashMessageService.updateMessage(e);
    });
  }

  selectSupplier() {
    this.supplierReadOnly = false;
  }
}
